import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import DataTable from 'react-data-table-component';
import { customAxiosInstanceMutator } from '../../../services/custom-axios-instance';
import CreditDirectorReportsModal from '../loan-processing/CreditDirectorReportsModal';
import { DirectorReportView } from '../loan-processing/creditDirectorReportsTypes';
import { formatDate } from '../../../utils/formatters';

interface CreditReport {
  applicationId: string;
  applicationNumber: string;
  customerName: string;
  customerBusinessName?: string;
  status: string;
  recommendation: string;
  riskLevel: string;
  approvedAmount: number;
  interestRate: number;
  termMonths: number;
  assessedBy?: string;
  assessedDate?: string;
  createdDate?: string;
  xdsReport?: Record<string, unknown>;
  scoringResult?: Record<string, unknown>;
}

interface CreditReportsPage {
  content: CreditReport[];
  totalElements: number;
  totalPages: number;
  size: number;
  page: number;
}

const CreditReports = () => {
  const [searchName, setSearchName] = useState('');
  const [searchIdNumber, setSearchIdNumber] = useState('');
  const [reports, setReports] = useState<CreditReport[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalRows, setTotalRows] = useState(0);
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [showReportModal, setShowReportModal] = useState(false);
  const [selectedReport, setSelectedReport] = useState<CreditReport | null>(null);

  const fetchReports = async (pageNum = 0, size = 10, name = '', idNumber = '') => {
    setIsLoading(true);
    try {
      const params: Record<string, any> = { page: pageNum, size };
      if (name.trim()) params.customerName = name.trim();
      if (idNumber.trim()) params.idNumber = idNumber.trim();

      const response = await customAxiosInstanceMutator<CreditReportsPage>({
        url: '/loan/api/v1/credit-assessment-history',
        method: 'GET',
        params
      });

      setReports(response.content || []);
      setTotalRows(response.totalElements || 0);
    } catch (err: any) {
      console.error('Error fetching reports:', err);
      toast.error(err?.response?.data?.message || 'Failed to fetch reports');
      setReports([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchReports(page, perPage, searchName, searchIdNumber);
  }, [page, perPage]);

  const handleSearch = () => {
    setPage(0);
    fetchReports(0, perPage, searchName, searchIdNumber);
  };

  const handleViewReport = async (report: CreditReport) => {
    setSelectedReport(report);
    setShowReportModal(true);
  };

  const getReportViews = (): DirectorReportView[] => {
    if (!selectedReport) return [];
    return [{
      id: selectedReport.applicationId,
      label: selectedReport.customerName || selectedReport.customerBusinessName || 'N/A',
      status: selectedReport.status === 'COMPLETED' ? 'SUCCESS' : 'UNKNOWN',
      creditScore: null,
      xdsReport: selectedReport.xdsReport || {},
      scoringEngineResult: selectedReport.scoringResult || {}
    }];
  };

  const columns = [
    {
      name: 'Name',
      selector: (row: CreditReport) => row.customerName || '',
      sortable: true,
      cell: (row: CreditReport) => (
        <div>
          <span className="fw-semibold">{row.customerName || 'N/A'}</span>
          {row.customerBusinessName && (
            <><br /><small className="text-muted">{row.customerBusinessName}</small></>
          )}
        </div>
      ),
      minWidth: '180px',
    },
    {
      name: 'Recommendation',
      selector: (row: CreditReport) => row.recommendation || '',
      sortable: true,
      cell: (row: CreditReport) => (
        <span className={`badge px-2 py-1 ${
          row.recommendation === 'APPROVE' ? 'bg-success' :
          row.recommendation === 'REJECT' || row.recommendation === 'DECLINE' ? 'bg-danger' : 'bg-warning text-dark'
        }`}>
          {row.recommendation || 'N/A'}
        </span>
      ),
      minWidth: '140px',
    },
    {
      name: 'Risk Band',
      selector: (row: CreditReport) => row.riskLevel || '',
      sortable: true,
      cell: (row: CreditReport) => (
        <span className={`badge px-2 py-1 ${
          row.riskLevel === 'LOW' ? 'bg-success-subtle text-success-emphasis' :
          row.riskLevel === 'MEDIUM' ? 'bg-warning-subtle text-warning-emphasis' :
          'bg-danger-subtle text-danger-emphasis'
        }`}>
          {row.riskLevel || 'N/A'}
        </span>
      ),
      minWidth: '120px',
    },
    {
      name: 'Probability',
      selector: (row: CreditReport) => row.interestRate || 0,
      sortable: true,
      cell: (row: CreditReport) => (
        <span className="fw-bold">{row.interestRate != null ? `${row.interestRate}%` : 'N/A'}</span>
      ),
      minWidth: '110px',
    },
    {
      name: 'Date',
      selector: (row: CreditReport) => row.assessedDate || row.createdDate || '',
      sortable: true,
      cell: (row: CreditReport) => (
        <span>{row.assessedDate ? formatDate(row.assessedDate) : row.createdDate ? formatDate(row.createdDate) : 'N/A'}</span>
      ),
      minWidth: '120px',
    },
    {
      name: 'Action',
      cell: (row: CreditReport) => (
        <button
          className="btn btn-sm btn-primary rounded-pill px-3"
          onClick={() => handleViewReport(row)}
        >
          <i className="fa fa-eye me-1"></i>
          View Report
        </button>
      ),
      minWidth: '140px',
    },
  ];

  return (
    <div>
      <div className="page-toolbar px-xl-4 px-sm-2 px-0 py-3">
        <div className="container-fluid">
          <div className="row g-3 mb-3 align-items-center">
            <div className="col">
              <ol className="breadcrumb bg-transparent mb-0">
                <li className="breadcrumb-item"><Link className="text-secondary" to="/">Home</Link></li>
                <li className="breadcrumb-item active" aria-current="page">Credit Reports</li>
              </ol>
            </div>
          </div>
        </div>
      </div>

      <div className="page-body px-xl-4 px-sm-2 px-0 py-lg-2 py-1 mt-0 mt-lg-3">
        <div className="container-fluid">
          {/* Search */}
          <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: '12px' }}>
            <div className="card-body p-4">
              <div className="row g-3 align-items-end">
                <div className="col-12 col-md-5">
                  <label className="form-label fw-semibold">
                    <i className="fa fa-user me-2 text-muted"></i>Search by Name
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter individual's name..."
                    value={searchName}
                    onChange={(e) => setSearchName(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  />
                </div>
                <div className="col-12 col-md-4">
                  <label className="form-label fw-semibold">
                    <i className="fa fa-id-card me-2 text-muted"></i>Search by ID Number
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter ID number..."
                    value={searchIdNumber}
                    onChange={(e) => setSearchIdNumber(e.target.value.replace(/\D/g, '').slice(0, 13))}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    maxLength={13}
                  />
                </div>
                <div className="col-12 col-md-3">
                  <div className="d-flex gap-2">
                    <button className="btn btn-primary flex-grow-1" onClick={handleSearch}>
                      <i className="fa fa-search me-2"></i>Search
                    </button>
                    <button
                      className="btn btn-outline-secondary"
                      onClick={() => {
                        setSearchName('');
                        setSearchIdNumber('');
                        setPage(0);
                        fetchReports(0, perPage, '', '');
                      }}
                    >
                      <i className="fa fa-refresh"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="card border-0 shadow-sm" style={{ borderRadius: '12px' }}>
            <div className="card-body p-0">
              <DataTable
                columns={columns}
                data={reports}
                progressPending={isLoading}
                pagination
                paginationServer
                paginationTotalRows={totalRows}
                paginationPerPage={perPage}
                onChangePage={(p) => setPage(p - 1)}
                onChangeRowsPerPage={(newPerPage) => {
                  setPerPage(newPerPage);
                  setPage(0);
                }}
                highlightOnHover
                striped
                noDataComponent={
                  <div className="text-center py-5">
                    <i className="fa fa-file-text fa-3x text-muted mb-3" style={{ display: 'block' }}></i>
                    <h6 className="text-muted">No Reports Found</h6>
                    <p className="text-muted small">Run a credit assessment to generate reports.</p>
                  </div>
                }
                customStyles={{
                  headRow: {
                    style: {
                      backgroundColor: '#f8f9fa',
                      borderBottom: '2px solid #dee2e6',
                      fontWeight: 700,
                    },
                  },
                  rows: {
                    style: {
                      minHeight: '60px',
                    },
                  },
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Report Modal */}
      <CreditDirectorReportsModal
        show={showReportModal}
        onHide={() => setShowReportModal(false)}
        reports={getReportViews()}
        selectedIndex={0}
        onSelectIndex={() => {}}
        processedCount={selectedReport ? 1 : 0}
        successCount={selectedReport ? 1 : 0}
        failedCount={0}
        showDownloadControl={true}
        downloadEnabled={true}
      />
    </div>
  );
};

export default CreditReports;
